import {
  Button,
  Stack,
  Title,
  TextInput,
  Alert,
  Group,
  Anchor,
  Text,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { forgotPassword } from '../../services/api';
import { IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { LoginWrapper } from '../LoginWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { primaryColor } from '../../constants/theme';
import { useState } from 'react';
import { MobileLogo } from './SignUpEmail';

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = (location.state as { email?: string })?.email ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    mutate: sendReset,
    isPending,
    isError,
    reset: resetMutation,
  } = useMutation({
    mutationFn: () => forgotPassword({ email }),
    onSuccess: () => setSuccess(true),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    resetMutation();
    if (!/^\S+@\S+$/.test(email)) {
      setEmailError('Invalid email');
      return;
    }
    setEmailError(null);
    sendReset();
  };

  return (
    <LoginWrapper animate={false}>
      <form onSubmit={handleSubmit}>
        <MobileLogo />
        <Stack>
          <Group justify="space-between" align="center">
            <Title order={2} ta="left" data-testid={ForgotPasswordTestIds.TITLE}>
              Forgot Password
            </Title>
            <Anchor
              data-testid={ForgotPasswordTestIds.BACK_BUTTON}
              c="dimmed"
              onClick={() => navigate(-1)}
            >
              Back
            </Anchor>
          </Group>
          <Text c="dimmed" size="sm" ta='left'>
            Enter your email address and we'll send you a link to reset your password.
          </Text>
          <TextInput
            data-testid={ForgotPasswordTestIds.EMAIL_INPUT}
            radius={'lg'}
            size="md"
            placeholder="Your email"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
              setEmailError(null);
            }}
            error={emailError}
          />
          {success && (
            <Alert
              radius={'lg'}
              variant="filled"
              color={primaryColor}
              title="Email sent"
              icon={<IconCheck />}
              data-testid={ForgotPasswordTestIds.SUCCESS_ALERT}
            >
              If an account exists for that email, a reset link has been sent.
            </Alert>
          )}
          {isError && (
            <Alert
              radius={'lg'}
              variant="filled"
              color="red"
              title="Error"
              icon={<IconInfoCircle />}
              data-testid={ForgotPasswordTestIds.ERROR_ALERT}
            >
              There was an error sending the reset email. Please try again.
            </Alert>
          )}
          <Button
            fullWidth
            data-testid={ForgotPasswordTestIds.SUBMIT_BUTTON}
            color={primaryColor}
            c={'white'}
            radius={'lg'}
            size={'md'}
            type="submit"
            loading={isPending}
            disabled={success}
          >
            SEND RESET LINK
          </Button>
        </Stack>
      </form>
    </LoginWrapper>
  );
};

export enum ForgotPasswordTestIds {
  TITLE = 'FORGOT_PASSWORD_TITLE',
  BACK_BUTTON = 'FORGOT_PASSWORD_BACK_BUTTON',
  EMAIL_INPUT = 'FORGOT_PASSWORD_EMAIL_INPUT',
  SUBMIT_BUTTON = 'FORGOT_PASSWORD_SUBMIT_BUTTON',
  SUCCESS_ALERT = 'FORGOT_PASSWORD_SUCCESS',
  ERROR_ALERT = 'FORGOT_PASSWORD_ERROR',
}
